import { Box, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import useAPI from "../../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../../api/hooks/useColors";
import { entityKeys } from "../../../../../../../api/queryKeys";
import PrimaryButton from "../../../../../../../components/buttons/PrimaryButton";
import SwitchButton from "../../../../../../../components/filters/SwitchButton";
import LoadingIcon from "../../../../../../../components/LoadingIcon";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import { isLocalhost } from "../../../../../../../utils/utils";
import { severities } from "../../../../icons/iconExport";
import CustomCveBarChart from "./CustomCveBarChart";
import CvesChartLegend from "./CvesChartLegend";

// Count the number of vulnerabilities for each vendor type
const countVulnerabilities = (
  entityHostVulnerabilities,
  vendorTypes,
  countOfEntities
) => {
  const cves = vendorTypes.reduce((acc, vendor) => {
    acc[vendor] = {
      high: 0,
      critical: 0,
      entity_ids_high: new Set(),
      entity_ids_critical: new Set(),
      all_entity_ids: new Set(),
    };
    return acc;
  }, {});

  if (!entityHostVulnerabilities) return cves;

  Object.entries(entityHostVulnerabilities).forEach(
    ([entity_id, vulnerabilities]) => {
      vulnerabilities?.forEach((vulnerability) => {
        vendorTypes.forEach((vendor) => {
          if (
            vulnerability?.vendor_type
              ?.toLowerCase()
              ?.includes(vendor.toLowerCase())
          ) {
            if (vulnerability.severity === "HIGH") {
              cves[vendor].high += 1;
              cves[vendor].entity_ids_high.add(entity_id);
            } else if (vulnerability.severity === "CRITICAL") {
              cves[vendor].critical += 1;
              cves[vendor].entity_ids_critical.add(entity_id);
            }
            cves[vendor].all_entity_ids.add(entity_id);
          }
        });
      });
    }
  );
  // hack: the reason is that we sometimes consider thunderbird as firefox
  if (Object.keys(cves).includes("Firefox")) {
    // Clean up the Firefox hack with proper rounding
    if (Object.keys(cves).includes("Firefox")) {
      // Apply the 80% reduction factor
      cves["Firefox"].high = Math.round(cves["Firefox"].high * 0.8);
      cves["Firefox"].critical = Math.round(cves["Firefox"].critical * 0.8);
    }
  }

  // Calculate the average and format to one decimal place
  vendorTypes.forEach((vendor) => {
    const allEntityCount =
      cves[vendor]?.all_entity_ids?.size ||
      cves[vendor]?.all_entity_ids?.length ||
      0;
    cves[vendor].high = allEntityCount
      ? (cves[vendor].high / allEntityCount).toFixed(1)
      : "0.0";
    cves[vendor].critical = allEntityCount
      ? (cves[vendor].critical / allEntityCount).toFixed(1)
      : "0.0";
  });

  return cves;
};
// Format number to one decimal place if it is a float
const formatNumber = (number) => {
  return number % 1 === 0 ? number.toFixed(0) : number.toFixed(1);
};

const VulnerabilityManagementChart = ({
  entities,
  appliedFilters,
  setAppliedFilters,
}) => {
  const { trackEvent } = useAPI();
  const [barByEntity, setBarByEntity] = useState(false);
  const hostEntities = entities.filter(
    (entity) => entity.entity_type === "host"
  );
  const hostEntitiesLength = entities.filter(
    (entity) => entity.entity_type === "host"
  ).length;

  const [tabIndex, setTabIndex] = useState(0);
  const colors = useColors();
  const { fetchVulnerabilitiesByEntityType } = useAPI();

  // Check if running in localhost
  const isLocalhostEnv = isLocalhost();
  const [cvesFetchDisabled, setCvesFetchDisabled] = useState(true);

  const {
    data: entityHostVulnerabilities,
    isLoading: entityHostVulnerabilitiesIsLoading,
    refetch: refetchVulnerabilities,
  } = useQuery({
    queryKey: entityKeys.vulnerabilitiesByEntityType("host"),
    queryFn: () => {
      return fetchVulnerabilitiesByEntityType("host");
    },
    select: (response) => response?.data,
    enabled: !isLocalhostEnv || !cvesFetchDisabled,
  });
  const entitiesHostVulnerabilitiesFiltered = useMemo(() => {
    if (!entityHostVulnerabilities) return null;
    return Object.entries(entityHostVulnerabilities).reduce(
      (acc, [entity_id, vulnerabilities]) => {
        if (hostEntities.some((entity) => entity.entity_id === entity_id)) {
          acc[entity_id] = vulnerabilities;
        }
        return acc;
      },
      {}
    );
  }, [entityHostVulnerabilities, hostEntities]);

  const createDataSetFormat = (vendorType, cves) => {
    const dataList = [
      {
        data: barByEntity
          ? vendorType.map(
              (vendor) => Array.from(cves[vendor]?.entity_ids_high).length
            )
          : vendorType.map((vendor) => parseFloat(cves[vendor].high)),
        color: colors.cve_high,
        entity_ids: vendorType.map((vendor) =>
          Array.from(cves[vendor].entity_ids_high)
        ),
      },
      {
        data: barByEntity
          ? vendorType.map(
              (vendor) => Array.from(cves[vendor]?.entity_ids_critical).length
            )
          : vendorType.map((vendor) => parseFloat(cves[vendor].critical)),
        color: colors.cve_critical,
        entity_ids: vendorType.map((vendor) =>
          Array.from(cves[vendor].entity_ids_critical)
        ),
      },
    ];
    return dataList;
  };

  const vendorTypesOs = ["Windows", "Mac"];

  const vendorTypesBrowser = ["Safari", "Chrome", "Firefox", "Edge"];

  // Get third party vendors
  const getThirdPartyVendors = (entitiesHostVulnerabilitiesFiltered) => {
    const allVendors = new Set();

    Object.values(entityHostVulnerabilities || {}).forEach(
      (vulnerabilities) => {
        vulnerabilities?.forEach((vulnerability) => {
          if (vulnerability.vendor_type) {
            allVendors.add(vulnerability.vendor_type);
          }
        });
      }
    );
    const ThirdParty = Array.from(allVendors).filter((vendor) => {
      const vendorStart = vendor.split(" ")[0];
      return (
        !vendorTypesOs.includes(vendorStart) &&
        !vendorTypesBrowser.includes(vendorStart)
      );
    });
    return ThirdParty;
  };
  // Get top 5 vendors
  const allVendorTypesThirdParty = useMemo(() => {
    const allVendors = getThirdPartyVendors(entityHostVulnerabilities);
    return allVendors;
  }, [entitiesHostVulnerabilitiesFiltered]);
  // Get other vendors
  const otherVendorTypesThirdParty = useMemo(() => {
    const topFiveVendors = getThirdPartyVendors(
      entityHostVulnerabilities
    ).slice(5);
    return topFiveVendors;
  }, [entitiesHostVulnerabilitiesFiltered]);

  // Count the number of vulnerabilities for each vendor type
  const osCount = useMemo(() => {
    if (!entitiesHostVulnerabilitiesFiltered) return null;
    const cves = countVulnerabilities(
      entitiesHostVulnerabilitiesFiltered,
      vendorTypesOs,
      hostEntitiesLength
    );
    return createDataSetFormat(vendorTypesOs, cves);
  }, [entitiesHostVulnerabilitiesFiltered]);

  const browserCount = useMemo(() => {
    if (!entitiesHostVulnerabilitiesFiltered) return null;
    const cves = countVulnerabilities(
      entitiesHostVulnerabilitiesFiltered,
      vendorTypesBrowser,
      1
    );
    return createDataSetFormat(vendorTypesBrowser, cves);
  }, [entitiesHostVulnerabilitiesFiltered]);

  // Get vendors with the highest vulnerability counts
  function getTopVendorsByVulnerabilityCount(cves, vendorTypes, limit = 5) {
    if (!cves || !vendorTypes || vendorTypes.length === 0) {
      return [];
    }

    // Calculate combined vulnerability counts for each vendor
    const vendorsWithCounts = vendorTypes.map((vendor) => ({
      name: vendor,
      count:
        parseFloat(cves[vendor]?.high || 0) +
        parseFloat(cves[vendor]?.critical || 0),
      highCount: parseFloat(cves[vendor]?.high || 0),
      criticalCount: parseFloat(cves[vendor]?.critical || 0),
    }));

    // Sort vendors by total count (high + critical) in descending order
    const sortedVendors = vendorsWithCounts.sort((a, b) => b.count - a.count);

    // Return the top N vendors or all if fewer than limit
    return sortedVendors.slice(0, limit).map((vendor) => vendor.name);
  }

  // In your VulnerabilityManagementChart component, use this function for third-party vendors:
  const vendorTypesThirdPartyTopFive = useMemo(() => {
    if (!entitiesHostVulnerabilitiesFiltered) return [];

    const cves = countVulnerabilities(
      entitiesHostVulnerabilitiesFiltered,
      allVendorTypesThirdParty,
      1
    );

    return getTopVendorsByVulnerabilityCount(cves, allVendorTypesThirdParty);
  }, [entitiesHostVulnerabilitiesFiltered, allVendorTypesThirdParty]);

  // Then update your thirdPartyCount useMemo:
  const thirdPartyCount = useMemo(() => {
    if (!entitiesHostVulnerabilitiesFiltered) return null;

    const cves = countVulnerabilities(
      entitiesHostVulnerabilitiesFiltered,
      allVendorTypesThirdParty,
      1
    );

    // Get the top 5 vendors and create an "Other" category for the rest
    const topVendors = vendorTypesThirdPartyTopFive;
    const otherVendors = allVendorTypesThirdParty.filter(
      (vendor) => !topVendors.includes(vendor)
    );

    // Aggregate counts for "Other" vendors
    const highOther = otherVendors.reduce(
      (acc, vendor) => acc + parseFloat(cves[vendor]?.high || 0),
      0
    );

    const criticalOther = otherVendors.reduce(
      (acc, vendor) => acc + parseFloat(cves[vendor]?.critical || 0),
      0
    );

    const entityIdsHighOther = otherVendors.reduce(
      (acc, vendor) =>
        new Set([...acc, ...(cves[vendor]?.entity_ids_high || [])]),
      new Set()
    );

    const entityIdsCriticalOther = otherVendors.reduce(
      (acc, vendor) =>
        new Set([...acc, ...(cves[vendor]?.entity_ids_critical || [])]),
      new Set()
    );

    // Add "Other" category if we have more than 5 vendors
    if (otherVendors.length > 0) {
      cves.Other = {
        high: highOther,
        critical: criticalOther,
        entity_ids_high: entityIdsHighOther,
        entity_ids_critical: entityIdsCriticalOther,
      };
      // Create final display list with top vendors plus "Other"
      const displayVendors = [...topVendors, "Other"];
      return createDataSetFormat(displayVendors, cves);
    }

    // If we have 5 or fewer vendors, just use them all
    return createDataSetFormat(topVendors, cves);
  }, [
    entitiesHostVulnerabilitiesFiltered,
    allVendorTypesThirdParty,
    vendorTypesThirdPartyTopFive,
  ]);

  const calculateUniqueAffectedEntities = (dataSet) => {
    if (!dataSet || dataSet.length === 0) return 0;

    // Create a single Set from all entity IDs (both high and critical)
    const uniqueEntityIds = new Set();

    // Add all high severity entity IDs
    dataSet[0].entity_ids.forEach((entityArray) => {
      entityArray.forEach((entityId) => uniqueEntityIds.add(entityId));
    });

    // Add all critical severity entity IDs
    dataSet[1].entity_ids.forEach((entityArray) => {
      entityArray.forEach((entityId) => uniqueEntityIds.add(entityId));
    });

    return uniqueEntityIds.size;
  };
  const cvesChartData = [
    {
      tabTitle: "OS CVEs",
      title: "Average of OS Vulnerabilities Per Host",
      labels: vendorTypesOs,
      series: osCount,
      tabIndex: 0,
      affectedEntities: calculateUniqueAffectedEntities(osCount),
    },
    {
      tabTitle: "Browser CVEs",
      title: barByEntity
        ? "Number of host with Browser Vulnerabilities"
        : "Number of Browser Vulnerabilities",
      labels: vendorTypesBrowser,
      series: browserCount,
      tabIndex: 1,
      averageHeader: true,
      affectedEntities: calculateUniqueAffectedEntities(browserCount),
    },
    {
      tabTitle: "3rd Party CVEs",
      title: barByEntity
        ? "Number of host with 3rd Party Vulnerabilities"
        : "Number of 3rd Party Vulnerabilities",
      labels: [...vendorTypesThirdPartyTopFive, "Other"],
      series: thirdPartyCount,
      tabIndex: 2,
      affectedEntities: calculateUniqueAffectedEntities(thirdPartyCount),
    },
  ];

  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={(e, newIndex) => {
          setTabIndex(newIndex);
          setBarByEntity(false);
        }}
        textColor="primary"
        TabIndicatorProps={{
          style: {
            backgroundColor: colors.primary_main,
          },
        }}
        sx={{
          width: "100%",

          "& .MuiTabs-flexContainer": {
            backgroundColor: colors.common_white,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 400,
            color: colors.text_secondary,
            padding: "12px 16px",
          },
        }}
      >
        {cvesChartData.map((chartData, index) => (
          <Tab
            key={index}
            label={chartData.tabTitle}
            sx={{
              "&.Mui-selected": {
                color: colors.text_primary,
                fontWeight: 500,
              },
            }}
          />
        ))}
      </Tabs>
      {cvesFetchDisabled && isLocalhostEnv ? (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="body1">
            CVE data fetching is currently disabled.
          </Typography>
          <PrimaryButton
            variant="contained"
            onClick={() => {
              setCvesFetchDisabled(false);
              refetchVulnerabilities();
              trackEvent("User enabled CVE data fetching in localhost");
            }}
          >
            Enable CVE Data Fetching
          </PrimaryButton>
        </Box>
      ) : entityHostVulnerabilitiesIsLoading ? (
        <Box sx={{ p: 3 }}>
          <LoadingIcon size={12} />
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          {cvesChartData.map((chartData, index) => {
            return (
              <Box key={index}>
                {tabIndex === chartData.tabIndex && (
                  <Box
                    sx={{
                      background: colors.common_white,
                      boxShadow: "0px 0px 6.3px 0px " + colors.grey_24percent,
                      borderRadius: "8px",
                      width: "100%",
                      height: "430px",
                      p: 2,
                    }}
                  >
                    {/* Title */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="subtitle1">
                          {chartData.title}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption">Display by</Typography>
                          <SwitchButton
                            selectedLogic={
                              barByEntity ? "entities" : "vulnerabilities"
                            }
                            onChange={(logic) => {
                              if (logic === "vulnerabilities") {
                                trackEvent(
                                  `User toggled to show bar by${
                                    barByEntity ? "Vulnerabilities" : "entities"
                                  } on Vulnerability Management Chart`
                                );
                                setBarByEntity(false);
                              } else setBarByEntity(true);
                            }}
                            logicsAndLabels={{
                              vulnerabilities: "Vulnerabilities",
                              entities: "Entities",
                            }}
                            sx={{
                              width: "200px",
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <SvgToIcon
                            IconLogo={severities.CriticalSeverityIcon}
                            style={{
                              width: "16px",
                            }}
                          />
                          <Typography variant="overline">
                            {formatNumber(
                              chartData.series[1].data.reduce(
                                (acc, item) => acc + item,
                                0
                              )
                            )}
                          </Typography>
                          {chartData?.averageHeader && !barByEntity && (
                            <Typography variant="overline">
                              {"(" +
                                formatNumber(
                                  chartData.series[1].data.reduce(
                                    (acc, item) => acc + item,
                                    0
                                  ) / hostEntitiesLength
                                ) +
                                ")"}
                            </Typography>
                          )}
                          <Typography variant="caption">Critical</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <SvgToIcon
                            IconLogo={severities.HighSeverityIcon}
                            style={{
                              width: "16px",
                            }}
                          />

                          <Typography variant="overline">
                            {formatNumber(
                              chartData.series[0].data.reduce(
                                (acc, item) => acc + item,
                                0
                              )
                            )}
                          </Typography>
                          {chartData?.averageHeader && !barByEntity && (
                            <Typography variant="overline">
                              {"(" +
                                formatNumber(
                                  chartData.series[0].data.reduce(
                                    (acc, item) => acc + item,
                                    0
                                  ) / hostEntitiesLength
                                ) +
                                ")"}
                            </Typography>
                          )}
                          <Typography variant="caption">High</Typography>
                        </Box>

                        <Divider orientation="vertical" flexItem />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="overline">
                            {chartData.affectedEntities}
                          </Typography>
                          <Typography variant="caption">
                            Affected Entities
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {/* Chart */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        pt: 1,
                      }}
                    >
                      {osCount && (
                        <CustomCveBarChart
                          series={chartData.series}
                          labels={chartData.labels}
                          appliedFilters={appliedFilters}
                          setAppliedFilters={setAppliedFilters}
                        />
                      )}
                      <CvesChartLegend />
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default VulnerabilityManagementChart;
